<template>
	<app-content class="h-panel account-page">
		<!-- 顶部筛选栏 -->
		<div class="filter-bar">
			<Button color="primary" icon="h-icon-plus" @click="openCreate">创建账套</Button>
			<input type="text" class="filter-input" v-model="filters.companyName" placeholder="单位名称" @keyup.enter="search">
			<input type="text" class="filter-input" v-model="filters.creditCode" placeholder="税号" @keyup.enter="search">
			<Select v-model="filters.accountingStandards" class="filter-select" clearable placeholder="会计准则"
				:datas="standardsOptions" keyName="key" titleName="title"></Select>
			<Select v-model="filters.vatType" class="filter-select" clearable placeholder="增值税种类"
				:datas="vatOptions" keyName="key" titleName="title"></Select>
			<Button @click="search">检索</Button>
			<Button @click="resetFilters">重置</Button>
			<div class="filter-right">
				<Checkbox v-model="showDisabled">显示已停用</Checkbox>&nbsp;&nbsp;
				<Button @click="openBackup">备份记录</Button>
			</div>
		</div>

		<div class="h-panel-body">
			<Table :datas="datas" :border="true">
				<TableItem title="#" :width="50" align="center">
					<template slot-scope="{index}">{{index + 1}}</template>
				</TableItem>
				<TableItem title="单位名称">
					<template slot-scope="{data}">
						<div class="company-cell">
							<span class="company-name">{{data.companyName}}</span>
							<span class="company-sub">（{{standardsMap[data.accountingStandards]}} - {{vatMap[data.vatType]}}）</span>
						</div>
					</template>
				</TableItem>
				<TableItem title="统一社会信用代码" prop="creditCode"></TableItem>
				<TableItem title="当前记账年月" :width="110">
					<template slot-scope="{data}">{{monthFormat(data.currentAccountDate)}}</template>
				</TableItem>
				<TableItem title="到期时间" :width="110">
					<template slot-scope="{data}">
						<span v-if="data.expireDate" :class="expireClass(data.expireDate)">{{dateFormat(data.expireDate)}}</span>
						<span v-else class="gray-color">未设置</span>
					</template>
				</TableItem>
				<TableItem title="账套启用年月" :width="110">
					<template slot-scope="{data}">{{monthFormat(data.enableDate)}}</template>
				</TableItem>
				<TableItem title="凭证审核" :width="90">
					<template slot-scope="{data}">{{data.voucherReviewed == 1 ? '启用' : '不启用'}}</template>
				</TableItem>
				<TableItem title="账套创建人" prop="creatorMobile" :width="120"></TableItem>
				<TableItem title="我的角色" :width="100">
					<template slot-scope="{data}">{{rolesMap[data.myRole] || data.myRole}}</template>
				</TableItem>
				<TableItem title="创建时间" :width="150">
					<template slot-scope="{data}">{{timeFormat(data.createDate)}}</template>
				</TableItem>
				<TableItem title="账套ID" prop="accountId" :width="170"></TableItem>
				<TableItem title="操作" :width="200" align="center">
					<div class="row-actions" slot-scope="{data}">
						<span class="action-link" @click="openTax(data)">税务信息</span>
						<span class="action-link" @click="edit(data)">编辑</span>
						<span class="action-link" @click="openRenew(data)">续费</span>
						<DropdownMenu :datas="moreMenu(data)" trigger="click" placement="bottom-end"
									@click="(key, item) => moreAction(key, data)">
									<span class="action-link">更多 <i class="h-icon-chevron-down"></i></span>
								</DropdownMenu>
					</div>
				</TableItem>
			</Table>
		</div>

		<!-- 创建/编辑账套 -->
		<Modal v-model="showForm" type="drawer-right" hasCloseIcon>
			<div slot="header">{{form.id ? '编辑账套' : '创建账套'}}</div>
			<Form ref="form" v-width="760" mode="twocolumn" :labelWidth="160" :model="form" :rules="validationRules">
				<FormItem label="账套名称:" prop="companyName">
					<input type="text" v-model="form.companyName" placeholder="请输入单位名称">
				</FormItem>
				<FormItem label="社会信用代码:" prop="creditCode">
					<input type="text" v-model="form.creditCode">
				</FormItem>
				<FormItem label="账套启用年月:" prop="enableDate">
					<DatePicker type="month" v-model="form.enableDate" :disabled="used"></DatePicker>
				</FormItem>
				<FormItem label="账套模板:" prop="templateKey">
					<Select v-model="form.templateKey" :datas="templateOptions" keyName="key" titleName="title"
					:disabled="!!form.id" placeholder="请选择账套模板" @change="onTemplateChange"></Select>
					<template v-if="!form.id">
						<p class="h-text-help" style="margin-top:5px;">创建后将按模板对应的会计准则自动初始化会计科目及该准则的全部标准报表（资产负债表、利润表、现金流量表、所有者权益变动表、政府会计预算报表等）</p>
					</template>
				</FormItem>
				<FormItem label="会计准则:" prop="accountingStandards">
					<Select v-model="form.accountingStandards" dict="accountingStandards" disabled
						placeholder="由账套模板自动带出"></Select>
				</FormItem>
				<FormItem label="增值税种类:" prop="vatType">
					<Radio v-model="form.vatType" dict="vatRadios" disabled></Radio>
					<template v-if="form.id && form.vatType == 0">
						&nbsp;&nbsp;
						<Button size="s" color="primary" @click="upgradeVat">升级为一般纳税人</Button>
					</template>
				</FormItem>
				<FormItem label="凭证现金流平衡检查:">
					<Select v-model="form.cashFlowCheck" :datas="cashFlowChecks" keyName="id" titleName="name"></Select>
				</FormItem>
				<FormItem label="凭证审核:" prop="voucherReviewed">
					<Radio v-model="form.voucherReviewed" dict="needRadios"></Radio>
				</FormItem>
				<FormItem label="允许制单人与审核人为同一人:">
					<Radio v-model="form.sameMakerReviewer" dict="enableRadios"></Radio>
				</FormItem>
				<FormItem label="零申报:">
					<HSwitch v-model="form.zeroTax" :trueValue="1" :falseValue="0"></HSwitch>
				</FormItem>
			</Form>

			<div class="advanced-toggle">
				<a @click="showAdvanced = !showAdvanced">高级选项 <i :class="showAdvanced ? 'h-icon-chevron-up' : 'h-icon-chevron-down'"></i></a>
			</div>
			<div v-show="showAdvanced" class="advanced-body">
				<Form ref="advForm" v-width="760" mode="twocolumn" :labelWidth="160" :model="form">
					<FormItem label="单位所在地:">
						<CategoryPicker ref="CategoryPicker" :option="pickerOption" type="key" showAllLevels v-model="form.address"></CategoryPicker>
					</FormItem>
					<FormItem label="行业:">
						<Select v-model="form.industry" dict="industry"></Select>
					</FormItem>
					<FormItem label="固定资产模块:">
						<Radio v-model="form.fixedAssetModule" dict="enableRadios"></Radio>
					</FormItem>
					<FormItem label="资金模块:">
						<Radio v-model="form.fundsModule" dict="enableRadios"></Radio>
					</FormItem>
					<FormItem label="工资模块:">
						<Radio v-model="form.salaryModule" dict="enableRadios"></Radio>
					</FormItem>
				</Form>
			</div>

			<div class="text-center margin-top">
				<Button color="green" @click="submit" :loading="loading">{{form.id ? '更新' : '创建'}}账套</Button>
				<Button @click="showForm=false">取消</Button>
			</div>
		</Modal>

		<!-- 账套续费 -->
		<Modal v-model="showRenew" hasCloseIcon :width="420">
			<div slot="header">账套续费 - {{renewRow.companyName}}</div>
			<div class="margin">
				<p>当前到期时间：<b>{{renewRow.expireDate ? dateFormat(renewRow.expireDate) : '未设置'}}</b></p>
				<p class="gray-color" style="margin: 6px 0 10px;">请输入续订激活码，续费成功后到期时间自动顺延。</p>
				<input type="text" v-model.trim="renewCode" placeholder="请输入激活码（如 XXXX-XXXX-XXXX-XXXX）"
					   maxlength="32" @keyup.enter="doRenew">
				<!-- 收款配置引导：展示价格与收款码，引导线下购买激活码 -->
				<div v-if="payConfig && (payConfig.price || payConfig.hasQr)" class="renew-pay">
					<div class="renew-pay-title">{{payConfig.title || '扫码购买激活码'}}</div>
					<div v-if="payConfig.price" class="renew-pay-price">¥ {{payConfig.price}}</div>
					<img v-if="payConfig.hasQr" :src="payQrUrl" alt="收款码" class="renew-pay-qr">
					<div class="gray-color" style="font-size: 12px;">付款后请联系客服获取激活码</div>
				</div>
			</div>
			<div class="text-center">
				<Button color="primary" :loading="renewing" @click="doRenew">立即续费</Button>
				<Button @click="showRenew=false">取消</Button>
			</div>
		</Modal>

		<!-- 删除账套 -->
		<Modal v-model="showDelete" hasCloseIcon>
			<div slot="header">删除账套</div>
			<div>
				<div>您正在删除账套：<b>{{rmData.companyName}}</b><br>为保障数据安全，请完成安全验证。</div>
				<div class="margin">
					<CaptchaVerificationCode ref="delCaptcha" v-model="msgCode" :mobile="User.mobile"/>
				</div>
			</div>
			<div class="text-center">
				<Button color="red" :disabled="!msgCode" @click="doRemove" :loading="loading">删除</Button>
				<Button @click="showDelete=false">取消</Button>
			</div>
		</Modal>

		<!-- 税务信息 -->
		<Modal v-model="showTax" hasCloseIcon :width="1080">
			<div slot="header">税务信息 - {{taxRow.companyName}}</div>
			<div class="tax-config">
				<Form v-width="1020" :labelWidth="130" :model="taxConfig" mode="twocolumn">
					<FormItem label="社保申报:">
						<Radio v-model="taxConfig.socialEnabled" dict="enableRadios"></Radio>
					</FormItem>
					<FormItem label="税务地区:">
						<Select v-model="taxConfig.region" :datas="regionOptions" clearable placeholder="请选择税务地区"></Select>
					</FormItem>
					<FormItem label="社保申报密码:">
						<input type="password" v-model="taxConfig.socialPassword" placeholder="不修改请留空">
					</FormItem>
				</Form>
			</div>
			<div class="tax-actions">
				<Button color="primary" size="s" @click="editTaxAccount(null)">添加账号</Button>
				<Button color="green" size="s" @click="saveTaxConfig" :loading="taxSaving">保存</Button>
				<Button size="s" @click="subscribe">立即订阅</Button>
			</div>
			<Table :datas="taxAccounts" :border="true">
				<TableItem title="默认" :width="70" align="center">
					<template slot-scope="{data}">
						<input type="radio" :checked="data.isDefault == 1" @click="setDefaultAccount(data)">
					</template>
				</TableItem>
				<TableItem title="账号" prop="account"></TableItem>
				<TableItem title="税务身份" prop="taxIdentity"></TableItem>
				<TableItem title="办税小号" prop="taxSmallNo"></TableItem>
				<TableItem title="关联员工" :width="100">
					<template slot-scope="{data}">{{employeeName(data.employeeId)}}</template>
				</TableItem>
				<TableItem title="登录状态" :width="90">
					<template slot-scope="{data}">
						<span :class="data.loginStatus == 1 ? 'login-ok' : 'login-no'">
							{{data.loginStatus == 1 ? '已登录' : '未登录'}}
						</span>
					</template>
				</TableItem>
				<TableItem title="最后登录时间" :width="150">
					<template slot-scope="{data}">{{timeFormat(data.lastLoginTime)}}</template>
				</TableItem>
				<TableItem title="返回消息" prop="returnMsg"></TableItem>
				<TableItem title="操作" :width="110" align="center">
					<div class="row-actions" slot-scope="{data}">
						<span class="action-link" @click="editTaxAccount(data)">编辑</span>
						<span class="action-link red-color" @click="deleteTaxAccount(data)">删除</span>
					</div>
				</TableItem>
			</Table>
		</Modal>

		<!-- 税局账号编辑 -->
		<Modal v-model="showTaxAccount" hasCloseIcon :width="620">
			<div slot="header">{{taxAccount.id ? '编辑税局账号' : '添加税局账号'}}</div>
			<Form ref="taxAccountForm" v-width="560" :labelWidth="110" :model="taxAccount" :rules="taxAccountRules">
				<FormItem label="账号:" prop="account">
					<input type="text" v-model="taxAccount.account">
				</FormItem>
				<FormItem label="密码:" :prop="taxAccount.id ? '' : 'password'">
					<input type="password" v-model="taxAccount.password" :placeholder="taxAccount.id ? '不修改请留空' : '请输入密码'">
				</FormItem>
				<FormItem label="税务身份:">
					<Select v-model="taxAccount.taxIdentity" :datas="identityOptions" clearable></Select>
				</FormItem>
				<FormItem label="办税小号:">
					<input type="text" v-model="taxAccount.taxSmallNo">
				</FormItem>
				<FormItem label="关联员工:">
					<Select v-model="taxAccount.employeeId" :datas="taxEmployees" keyName="id" titleName="name" clearable></Select>
				</FormItem>
				<FormItem label="办税人:">
					<input type="text" v-model="taxAccount.operator">
				</FormItem>
				<FormItem label="手机号:">
					<input type="text" v-model="taxAccount.phone">
				</FormItem>
			</Form>
			<div class="text-center margin-top">
				<Button color="green" @click="saveTaxAccount" :loading="taxSaving">保存</Button>
				<Button @click="showTaxAccount=false">取消</Button>
			</div>
		</Modal>

		<!-- 备份记录 -->
		<Modal v-model="showBackup" hasCloseIcon :width="760">
			<div slot="header">备份记录</div>
			<div class="backup-tip">亲，备份的数据包是加密不能查看的，下载后即可上传，并且支持恢复数据包中的数据哦~</div>
			<div class="tax-actions">
				<Button color="primary" size="s" @click="createBackup">新建备份</Button>
			</div>
			<Table :datas="backupList" :border="true">
				<TableItem title="备份名称" prop="name"></TableItem>
				<TableItem title="备份日期" :width="160">
					<template slot-scope="{data}">{{timeFormat(data.createDate)}}</template>
				</TableItem>
				<TableItem title="文件大小" :width="100">
					<template slot-scope="{data}">{{fileSize(data.fileSize)}}</template>
				</TableItem>
				<TableItem title="操作" :width="160" align="center">
					<div class="row-actions" slot-scope="{data}">
						<span class="action-link" @click="restoreBackup(data)">恢复</span>
						<span class="action-link red-color" @click="deleteBackup(data)">删除</span>
						<a class="action-link" :href="backupDownloadUrl(data)">下载</a>
					</div>
				</TableItem>
			</Table>
		</Modal>

		<!-- 用户授权 -->
		<Modal v-model="showAuth" hasCloseIcon :width="720">
			<div slot="header">用户授权 - {{authRow.companyName}}</div>
			<div class="tax-actions">
				<Button color="primary" size="s" @click="openAuthForm(null)">新增</Button>
			</div>
			<Table :datas="authUsers" :border="true">
				<TableItem title="用户" prop="realName"></TableItem>
				<TableItem title="手机号" prop="mobile"></TableItem>
				<TableItem title="权限" :width="110">
					<template slot-scope="{data}">{{rolesMap[data.role] || data.role}}</template>
				</TableItem>
				<TableItem title="操作" :width="110" align="center">
					<div class="row-actions" slot-scope="{data}">
						<template v-if="data.id != authRow.creatorId">
							<span class="action-link" @click="openAuthForm(data)">编辑</span>
							<span class="action-link red-color" @click="removeAuthUser(data)">删除</span>
						</template>
						<span v-else class="gray-color">创建人</span>
					</div>
				</TableItem>
			</Table>
		</Modal>

		<!-- 授权用户表单 -->
		<Modal v-model="showAuthForm" hasCloseIcon :width="520">
			<div slot="header">{{authForm.id ? '编辑权限' : '新增权限'}}</div>
			<Form ref="authFormRef" v-width="460" :labelWidth="90" :model="authForm" :rules="authRules">
				<FormItem label="手机用户:" prop="mobile">
					<input type="text" v-model="authForm.mobile" :disabled="!!authForm.id">
				</FormItem>
				<FormItem label="权限角色:" prop="role">
					<Radio v-model="authForm.role" :datas="roleList">
						<template slot="item" slot-scope="{item}">
							<span>{{item.title}}</span>
							<div class="role-desc">{{item.desc}}</div>
						</template>
					</Radio>
				</FormItem>
			</Form>
			<div class="text-center margin-top">
				<Button color="green" @click="saveAuthUser" :loading="authSaving">{{authForm.id ? '更新' : '保存'}}</Button>
				<Button @click="showAuthForm=false">取消</Button>
			</div>
		</Modal>

		<!-- 未注册用户确认 -->
		<Modal v-model="showNewAuthUser" hasCloseIcon :width="420">
			<div slot="header">新增权限</div>
			<div class="margin">
				亲，您输入的手机还没有注册，确认需要新增吗？<br>
				为保障您的数据安全，新注册用户需要验证手机。
			</div>
			<div class="margin h-input-group">
				<input type="text" v-model="authForm.mobile" disabled>
				<SmsVerificationCode :mobile="authForm.mobile"/>
			</div>
			<div class="margin">
				<input v-model="authMsgCode" type="text" placeholder="请输入验证码">
			</div>
			<div class="text-center">
				<Button color="green" @click="saveNewAuthUser" :loading="authSaving">确定</Button>
				<Button @click="showNewAuthUser=false">取消</Button>
			</div>
		</Modal>

		<!-- 客户微信 -->
		<Modal v-model="showWechat" hasCloseIcon :width="420">
			<div slot="header">客户微信</div>
			<div class="margin text-center">
				<i class="fa fa-weixin" style="font-size:48px;color:#1aad19"></i>
				<p>将账套推送到客户微信</p>
				<p class="gray-color">需先配置微信公众号服务号参数，<br>配置后可向客户推送账套与申报信息。</p>
			</div>
			<div class="text-center">
				<Button @click="showWechat=false">关闭</Button>
			</div>
		</Modal>

		<!-- 超级会员订购 -->
		<Modal v-model="showVipModal" hasCloseIcon>
			<div slot="header">服务订购</div>
			<div class="vip-modal">
				<div class="vip-body">
					<div class="vip-card">
						<div class="vip-card-row"><span class="vip-card-label">服务套餐</span><span class="vip-card-value">超级会员</span></div>
						<div class="vip-card-row"><span class="vip-card-label">购买时长</span><span class="vip-card-value">100年</span></div>
						<div class="vip-card-row"><span class="vip-card-label">成员人数</span><span class="vip-card-value">不限制</span></div>
						<div class="vip-card-row"><span class="vip-card-label">账套数量</span><span class="vip-card-value">不限制</span></div>
						<div class="vip-card-row"><span class="vip-card-label">服务支持</span><span class="vip-card-value">一对一服务</span></div>
						<div class="vip-card-row vip-card-price"><span class="vip-card-label">订单金额</span><span class="vip-card-value"><b class="vip-price">¥498</b><s class="vip-price-old">¥1600</s></span></div>
					</div>
					<div class="vip-pay" v-if="payment.hasQr">
						<div class="vip-pay-title">{{payment.title}}</div>
						<img :src="qrImgUrl" class="vip-pay-qr" alt="收款二维码"/>
						<div class="vip-pay-amount">¥{{payment.price}}</div>
						<template v-if="!order.orderNo">
							<Button color="primary" size="s" :loading="ordering" @click="createOrder">生成订单</Button>
						</template>
						<template v-else>
							<div class="vip-pay-order">订单号：{{order.orderNo}}</div>
							<div class="vip-pay-tip">请微信扫码支付，支付成功后自动开通</div>
						</template>
					</div>
					<div class="vip-pay" v-else>
						<div class="vip-pay-empty">暂未配置收款二维码，请使用激活码或联系客服</div>
					</div>
					<div class="vip-compare">
						<div class="vip-compare-title">特权对比</div>
						<table>
							<thead>
							<tr><th>功能特权</th><th>超级会员</th><th>免费版</th></tr>
							</thead>
							<tbody>
							<tr><td>账套数限制</td><td class="vip-yes">不限制</td><td>{{member.limit}}账套</td></tr>
							<tr><td>用户数限制</td><td class="vip-yes">不限制</td><td>1用户</td></tr>
							<tr><td>电子档案空间</td><td class="vip-yes">1000M</td><td>50M</td></tr>
							<tr><td>技术支持</td><td class="vip-yes">一对一</td><td>企业微信群</td></tr>
							<tr><td>应用中心</td><td class="vip-yes">VIP应用免费</td><td>按应用付费</td></tr>
							</tbody>
						</table>
					</div>
				</div>
				<div class="vip-activate">
					<input type="text" v-model.trim="activateCode" placeholder="请输入激活码" class="vip-activate-input"
					   maxlength="32" @keyup.enter="doActivate">
					<Button color="primary" :loading="activating" @click="doActivate">立即激活</Button>
				</div>
				<div class="vip-tip">暂无激活码？请联系客服获取激活码</div>
			</div>
		</Modal>
	</app-content>
</template>

<script>
	import {getTotalData} from '../../js/locations/district';
	import moment from 'moment';
	import {mapState} from 'vuex';
	import Ajax from '../../js/common/ajax';
	import SmsVerificationCode from "../../components/SmsVerificationCode";
	import CaptchaVerificationCode from "../../components/CaptchaVerificationCode";

	const emptyForm = {
		"accountingStandards": 0,
		"address": null,
		"companyName": "",
		"creditCode": "",
		"enableDate": null,
		"fixedAssetModule": 0,
		"cashierModule": 0,
		"fundsModule": 0,
		"salaryModule": 0,
		"voucherReviewed": 0,
		"sameMakerReviewer": 1,
		"templateKey": null,
		"industry": "",
		"vatType": 0,
		"zeroTax": 0,
		"cashFlowCheck": 0
	};

	//账套模板（与线上一致：会计准则 + 增值税种类组合）
	const templateOptions = [
		{key: 0, title: '小企业会计准则（2013年） - 小规模纳税人', standard: 0, vat: 0},
		{key: 1, title: '小企业会计准则（2013年） - 一般纳税人', standard: 0, vat: 1},
		{key: 2, title: '企业会计准则（2007年） - 小规模纳税人', standard: 1, vat: 0},
		{key: 3, title: '企业会计准则（2007年） - 一般纳税人', standard: 1, vat: 1},
		{key: 4, title: '民间非营利组织会计制度（2004年） - 小规模纳税人', standard: 2, vat: 0},
		{key: 5, title: '民间非营利组织会计制度（2004年） - 一般纳税人', standard: 2, vat: 1},
		{key: 6, title: '企业会计准则(2019年未执行新金融准则、新收入准则和新租赁准则) - 小规模纳税人', standard: 3, vat: 0},
		{key: 7, title: '企业会计准则(2019年未执行新金融准则、新收入准则和新租赁准则) - 一般纳税人', standard: 3, vat: 1},
		{key: 8, title: '企业会计准则(2019年已执行新金融准则、新收入准则和新租赁准则) - 小规模纳税人', standard: 4, vat: 0},
		{key: 9, title: '企业会计准则(2019年已执行新金融准则、新收入准则和新租赁准则) - 一般纳税人', standard: 4, vat: 1},
		{key: 10, title: '民间非营利组织会计制度（2026年） - 一般纳税人', standard: 2, vat: 1},
		{key: 11, title: '民间非营利组织会计制度（2026年） - 小规模纳税人', standard: 2, vat: 0},
		{key: 12, title: '农民专业合作社财务会计制度 - 小规模纳税人', standard: 5, vat: 0},
		{key: 13, title: '农民专业合作社财务会计制度 - 一般纳税人', standard: 5, vat: 1},
		{key: 14, title: '工会会计制度 - 小规模纳税人', standard: 6, vat: 0},
		{key: 15, title: '工会会计制度 - 一般纳税人', standard: 6, vat: 1},
		{key: 16, title: '政府会计制度（行政事业单位） - 小规模纳税人', standard: 7, vat: 0},
		{key: 17, title: '政府会计制度（行政事业单位） - 一般纳税人', standard: 7, vat: 1},
		{key: 18, title: '村集体经济组织会计制度 - 小规模纳税人', standard: 8, vat: 0},
		{key: 19, title: '村集体经济组织会计制度 - 一般纳税人', standard: 8, vat: 1}
	];

	const emptyTaxAccount = {
		id: null, account: '', password: '', taxIdentity: '', taxSmallNo: '',
		employeeId: null, operator: '', phone: ''
	};

	export default {
		name: 'Account',
		components: {SmsVerificationCode, CaptchaVerificationCode},
		data() {
			return {
				datas: [],
				//筛选
				filters: {companyName: '', creditCode: '', accountingStandards: '', vatType: ''},
				showDisabled: false,
				//账套表单
				pickerOption: {
					keyName: 'id',
					titleName: 'title',
					dataMode: 'list',
					parentName: 'parentId',
					datas: getTotalData()
				},
				form: Object.assign({}, emptyForm),
				showForm: false,
				validationRules: {
					required: ["companyName", "creditCode", "enableDate", "templateKey", "accountingStandards", "voucherReviewed", "vatType"]
				},
				showAdvanced: false,
				templateOptions: templateOptions,
				cashFlowChecks: [
					{id: 0, name: '不检查'},
					{id: 1, name: '只提醒'},
					{id: 2, name: '限制保存'}
				],
				used: false,
				loading: false,
				//续费
				showRenew: false,
				renewRow: {},
				renewCode: '',
				renewing: false,
				payConfig: null,
				payQrUrl: '',
				//删除
				showDelete: false,
				rmData: {},
				msgCode: '',
				//税务信息
				showTax: false,
				taxRow: {},
				taxConfig: {socialEnabled: 0, region: '', socialPassword: ''},
				taxAccounts: [],
				taxEmployees: [],
				taxSaving: false,
				//税局账号
				showTaxAccount: false,
				taxAccount: Object.assign({}, emptyTaxAccount),
				taxAccountRules: {required: ['account', 'password']},
				regionOptions: ['北京', '上海', '广东', '浙江', '江苏', '山东', '四川', '河南', '湖北', '福建', '湖南', '安徽'],
				identityOptions: ['法定代表人', '财务负责人', '办税员', '经办人'],
				//备份
				showBackup: false,
				backupList: [],
				//用户授权
				showAuth: false,
				authRow: {},
				authUsers: [],
				showAuthForm: false,
				authForm: {id: null, mobile: '', role: 'Manager'},
				authRules: {required: ['mobile', 'role'], mobile: ['mobile']},
				authSaving: false,
				showNewAuthUser: false,
				authMsgCode: '',
				roleList: [
					{key: 'Manager', title: '账套管理员', desc: '凭证 | 结账 | 账簿 | 报表 | 新增账套 | 删除账套'},
					{key: 'Making', title: '制单人', desc: '凭证 | 结账 | 账簿 | 报表 | 新增账套'},
					{key: 'View', title: '查看', desc: '查看凭证、账簿和报表 | 新增账套'}
				],
				//客户微信
				showWechat: false,
				//会员
				showVipModal: false,
				activateCode: '',
				activating: false,
				member: {vip: 0, limit: 2, used: 0},
				payment: {title: '微信扫码支付', price: 498, qrUrl: '', hasQr: false},
				order: {orderNo: '', amount: null},
				ordering: false,
				payTimer: null,
				//静态映射
				standardsMap: {
					0: '小企业会计准则',
					1: '企业会计准则',
					2: '民间非营利组织会计制度',
					3: '企业会计准则(2019未执行新准则)',
					4: '企业会计准则(2019已执行新准则)',
					5: '农民专业合作社财务会计制度',
					6: '工会会计制度',
					7: '政府会计制度（行政事业单位）',
					8: '村集体经济组织会计制度'
				},
				vatMap: {0: '小规模纳税人', 1: '一般纳税人'},
				rolesMap: {Manager: '账套管理员', Director: '主管', Making: '制单人', Cashier: '出纳', View: '查看', BOSS: '平台管理员'},
				standardsOptions: [
					{key: 0, title: '小企业会计准则'},
					{key: 1, title: '企业会计准则'},
					{key: 2, title: '民间非营利组织会计制度'},
					{key: 3, title: '企业会计准则(2019年未执行新金融准则、新收入准则和新租赁准则)'},
					{key: 4, title: '企业会计准则(2019年已执行新金融准则、新收入准则和新租赁准则)'},
					{key: 5, title: '农民专业合作社财务会计制度'},
					{key: 6, title: '工会会计制度'},
					{key: 7, title: '政府会计制度（行政事业单位）'},
					{key: 8, title: '村集体经济组织会计制度'}
				],
				vatOptions: [
					{key: 0, title: '小规模纳税人'},
					{key: 1, title: '一般纳税人'}
				]
			};
		},
		watch: {
			showForm(val) {
				if (!val) {
					this.reset();
				}
			},
			showVipModal(val) {
				if (val) {
					this.loadPaymentConfig();
				} else {
					this.stopPolling();
					this.order = {orderNo: '', amount: null};
				}
			},
			showDisabled() {
				this.loadList();
			}
		},
		computed: {
			...mapState(["User", "currentAccountSets"]),
			qrImgUrl() {
				return this.payment.qrUrl ? Ajax.PREFIX + this.payment.qrUrl : '';
			}
		},
		methods: {
			/* ===== 列表 ===== */
			loadList() {
				let params = {showDisabled: this.showDisabled ? 1 : 0};
				if (this.filters.companyName) params.companyName = this.filters.companyName;
				if (this.filters.creditCode) params.creditCode = this.filters.creditCode;
				if (this.filters.accountingStandards !== '') params.accountingStandards = this.filters.accountingStandards;
				if (this.filters.vatType !== '') params.vatType = this.filters.vatType;
				this.$api.setting.accountSets.list(params).then(({data}) => {
					this.datas = data || [];
				})
			},
			search() {
				this.loadList();
			},
			resetFilters() {
				this.filters = {companyName: '', creditCode: '', accountingStandards: '', vatType: ''};
				this.loadList();
			},

			/* ===== 创建/编辑 ===== */
			openCreate() {
				if (!this.member.vip && this.datas.length >= this.member.limit) {
					this.$Message.error(`免费用户可创建${this.member.limit}个，请升级超级会员`);
					this.showVipModal = true;
					return;
				}
				this.reset();
				this.showForm = true;
			},
			edit(row) {
				this.$api.setting.accountSets.checkUse(row.id).then(({data}) => {
					this.used = data;
					this.form = Object.assign({}, emptyForm, row);
					//根据会计准则+增值税种类反推账套模板
					let tpl = this.templateOptions.find(t => t.standard == row.accountingStandards && t.vat == row.vatType);
					this.form.templateKey = tpl ? tpl.key : null;
					this.showForm = true;
				})
			},
			//选择账套模板后自动带出会计准则和增值税种类
			onTemplateChange(key) {
				let tpl = this.templateOptions.find(t => t.key === key);
				if (tpl && !this.form.id) {
					this.form.accountingStandards = tpl.standard;
					this.form.vatType = tpl.vat;
				}
			},
			submit() {
				let validResult = this.$refs.form.valid();
				if (validResult.result) {
					this.loading = true;
					let params = Object.assign({}, this.form);
					delete params.templateKey;
					params.enableDate = moment(this.form.enableDate).format("YYYY-MM-DD");
					this.$api.setting.accountSets[this.form.id ? 'update' : 'save'](params).then(() => {
						this.$store.dispatch('init');
						this.loadList();
						this.showForm = false;
						this.loading = false;
					}).catch((res) => {
						this.loading = false;
						if (res && ((res.data && res.data.limitExceeded) || (res.msg && res.msg.indexOf('账套') > -1 && res.msg.indexOf('会员') > -1))) {
							this.showVipModal = true;
						}
					})
				}
			},
			upgradeVat() {
				this.$Confirm('确定将该账套升级为一般纳税人吗？').then(() => {
					let params = Object.assign({}, this.form);
					params.vatType = 1;
					params.enableDate = moment(this.form.enableDate).format("YYYY-MM-DD");
					this.$api.setting.accountSets.update(params).then(() => {
						this.$Message.success('已升级为一般纳税人');
						this.form.vatType = 1;
						this.loadList();
					})
				})
			},
			reset() {
				this.form = Object.assign({}, emptyForm);
				this.used = false;
				this.showAdvanced = false;
			},

			/* ===== 更多菜单 ===== */
			moreMenu(data) {
				return [
					{key: 'auth', title: '用户授权'},
					{key: 'wechat', title: '客户微信'},
					{key: 'status', title: data.status == 0 ? '启用账套' : '停用账套'},
					{key: 'upgrade', title: '升级会计准则'},
					{key: 'reset', title: '重置账套'},
					{key: 'delete', title: '删除账套'}
				];
			},
			moreAction(key, data) {
				switch (key) {
					case 'auth':
						this.openAuth(data);
						break;
					case 'wechat':
						this.showWechat = true;
						break;
					case 'status':
						this.toggleStatus(data);
						break;
					case 'upgrade':
						this.upgradeStandard(data);
						break;
					case 'reset':
						this.resetAccount(data);
						break;
					case 'delete':
						this.remove(data);
				}
			},
			toggleStatus(data) {
				let next = data.status == 0 ? 1 : 0;
				let tip = next == 1 ? '确定启用该账套吗？' : '确定停用该账套吗？停用后将不能进入该账套。';
				this.$Confirm(tip).then(() => {
					this.$api.setting.accountSets.status({asid: data.id, status: next}).then(() => {
						this.$Message.success(next == 1 ? '已启用' : '已停用');
						this.loadList();
					})
				})
			},
			upgradeStandard(data) {
				this.$Confirm('升级会计准则将按最新模板重建该账套的标准报表，并补齐当前准则缺失的科目；自定义报表与已录入凭证不受影响。确定继续吗？', {type: 'warning'}).then(() => {
					this.$api.setting.accountSets.upgradeStandard({asid: data.id}).then(({data: result}) => {
						let r = result || {};
						this.$Message.success(`升级完成：新增科目${r.addedSubjects || 0}个，生成报表${r.installedReports || 0}张`);
						this.$store.dispatch('init');
						this.loadList();
					})
				})
			},
			resetAccount(data) {
				this.$Confirm('确定重置账套吗？将清空该账套所有凭证数据，且不可恢复！', {type: 'warning'}).then(() => {
					this.$Confirm('再次确认：重置后账套回到启用初始状态！', {type: 'warning'}).then(() => {
						this.$api.setting.accountSets.reset({asid: data.id}).then(() => {
							this.$Message.success('账套已重置');
							this.loadList();
						})
					})
				})
			},

			/* ===== 续费 ===== */
			openRenew(data) {
				this.renewRow = data;
				this.renewCode = '';
				this.showRenew = true;
				//加载收款配置，引导用户购买激活码
				if (!this.payConfig) {
					this.$api.payment.config().then(({data: cfg}) => {
						this.payConfig = cfg;
						if (cfg && cfg.hasQr && cfg.qrUrl) {
							this.payQrUrl = Ajax.PREFIX + cfg.qrUrl;
						}
					}).catch(() => {
					});
				}
			},
			doRenew() {
				let code = (this.renewCode || '').trim().toUpperCase();
				if (!code) {
					this.$Message.error('请输入激活码');
					return;
				}
				this.renewing = true;
				this.$api.setting.accountSets.renew({code, accountSetsId: this.renewRow.id}).then(({msg}) => {
					this.$Message.success(msg || '续费成功');
					this.showRenew = false;
					this.renewing = false;
					this.loadList();
				}).catch(() => {
					this.renewing = false;
				})
			},

			/* ===== 删除 ===== */
			remove(data) {
				this.showDelete = true;
				this.rmData = data;
			},
			doRemove() {
				if (this.msgCode) {
					this.$Confirm("确认删除该账套？删除后不可恢复！").then(() => {
						this.$api.setting.accountSets.delete(this.rmData.id, this.msgCode, this.rmData.id).then(() => {
							this.loadList();
							this.$store.dispatch('init');
							this.showDelete = false;
							this.rmData = {};
							this.msgCode = '';
						}).catch(() => {
							//验证失败后刷新验证码，防止旧图被复用
							this.$refs.delCaptcha && this.$refs.delCaptcha.clear();
						})
					})
				}
			},

			/* ===== 税务信息 ===== */
			openTax(data) {
				this.taxRow = data;
				this.showTax = true;
				this.$api.setting.taxAccount.panel({asid: data.id}).then(({data: panel}) => {
					let cfg = panel.config || {};
					this.taxConfig = {
						socialEnabled: cfg.socialEnabled || 0,
						region: cfg.region || '',
						socialPassword: ''
					};
					this.taxAccounts = panel.accounts || [];
					this.taxEmployees = panel.employees || [];
				})
			},
			saveTaxConfig() {
				this.taxSaving = true;
				this.$api.setting.taxAccount.saveConfig(Object.assign({
					accountSetsId: this.taxRow.id
				}, this.taxConfig)).then(() => {
					this.$Message.success('配置已保存');
					this.taxConfig.socialPassword = '';
					this.taxSaving = false;
				}).catch(() => {
					this.taxSaving = false;
				})
			},
			subscribe() {
				this.$Message('订阅服务需开通税局直连，请联系客服');
			},
			editTaxAccount(data) {
				this.taxAccount = data
					? Object.assign({}, emptyTaxAccount, data, {password: ''})
					: Object.assign({}, emptyTaxAccount);
				this.showTaxAccount = true;
			},
			saveTaxAccount() {
				let validResult = this.$refs.taxAccountForm.valid();
				if (validResult.result) {
					this.taxSaving = true;
					this.$api.setting.taxAccount.save(Object.assign({
						accountSetsId: this.taxRow.id
					}, this.taxAccount)).then(() => {
						this.$Message.success('账号已保存');
						this.showTaxAccount = false;
						this.taxSaving = false;
						this.openTax(this.taxRow);
					}).catch(() => {
						this.taxSaving = false;
					})
				}
			},
			setDefaultAccount(data) {
				this.$api.setting.taxAccount.setDefault({id: data.id, asid: this.taxRow.id}).then(() => {
					this.openTax(this.taxRow);
				})
			},
			deleteTaxAccount(data) {
				this.$Confirm('确定删除该税局账号吗？').then(() => {
					this.$api.setting.taxAccount.delete(data.id, {asid: this.taxRow.id}).then(() => {
						this.openTax(this.taxRow);
					})
				})
			},
			employeeName(id) {
				if (!id) return '';
				let emp = this.taxEmployees.find(e => e.id === id);
				return emp ? emp.name : '';
			},

			/* ===== 备份记录 ===== */
			openBackup() {
				this.showBackup = true;
				this.loadBackups();
			},
			loadBackups() {
				this.$api.backup.list().then(({data}) => {
					this.backupList = data.results || data.records || data || [];
				})
			},
			createBackup() {
				this.$api.backup.create().then(() => {
					this.$Message.success('备份成功');
					this.loadBackups();
				})
			},
			restoreBackup(data) {
				this.$Confirm('确定恢复该备份数据吗？当前账套数据将被覆盖！').then(() => {
					this.$api.backup.restore(data.id).then(() => {
						this.$Message.success('恢复成功');
						this.loadBackups();
					})
				})
			},
			deleteBackup(data) {
				this.$Confirm('确定删除该备份吗？').then(() => {
					this.$api.backup.del(data.id).then(() => {
						this.loadBackups();
					})
				})
			},
			backupDownloadUrl(data) {
				return this.$api.backup.downloadUrl(data.id);
			},
			fileSize(size) {
				if (!size) return '0KB';
				if (size < 1024) return size + 'B';
				if (size < 1024 * 1024) return (size / 1024).toFixed(1) + 'KB';
				return (size / 1024 / 1024).toFixed(2) + 'MB';
			},

			/* ===== 用户授权 ===== */
			openAuth(data) {
				this.authRow = data;
				this.showAuth = true;
				this.$api.setting.accountSets.users({asid: data.id}).then(({data: users}) => {
					this.authUsers = users || [];
				})
			},
			openAuthForm(data) {
				this.authForm = data
					? {id: data.id, mobile: data.mobile, role: data.role}
					: {id: null, mobile: '', role: 'Manager'};
				this.showAuthForm = true;
			},
			saveAuthUser() {
				let validResult = this.$refs.authFormRef.valid();
				if (validResult.result) {
					this.authSaving = true;
					let params = Object.assign({asid: this.authRow.id}, this.authForm);
					let action = this.authForm.id ? 'updateUserRole' : 'addUser';
					if (this.authForm.id) {
						params.id = this.authForm.id;
					}
					this.$api.setting.accountSets[action](params).then(() => {
						this.$Message.success('保存成功');
						this.showAuthForm = false;
						this.authSaving = false;
						this.openAuth(this.authRow);
					}).catch((rs) => {
						this.authSaving = false;
						if (rs.code == 501) {
							this.showAuthForm = false;
							this.showNewAuthUser = true;
						}
					})
				}
			},
			saveNewAuthUser() {
				if (!this.authMsgCode) {
					this.$Message('请输入验证码！');
					return;
				}
				this.authSaving = true;
				this.$api.setting.accountSets.addNewUser(Object.assign({
					asid: this.authRow.id,
					code: this.authMsgCode
				}, this.authForm)).then(() => {
					this.$Message.success('新增成功');
					this.showNewAuthUser = false;
					this.authMsgCode = '';
					this.authSaving = false;
					this.openAuth(this.authRow);
				}).catch(() => {
					this.authSaving = false;
				})
			},
			removeAuthUser(data) {
				this.$Confirm('确定删除该用户的账套权限吗？').then(() => {
					this.$api.setting.accountSets.removeUser(data.id, {asid: this.authRow.id}).then(() => {
						this.openAuth(this.authRow);
					})
				})
			},

			/* ===== 会员 ===== */
			loadMemberInfo() {
				this.$api.member.info().then(({data}) => {
					this.member = data || {vip: 0, limit: 2, used: 0};
				})
			},
			loadPaymentConfig() {
				this.$api.payment.config().then(({data}) => {
					this.payment = data || {title: '微信扫码支付', price: 498, qrUrl: '', hasQr: false};
				}).catch(() => {
				})
			},
			createOrder() {
				this.ordering = true;
				this.$api.payment.createOrder().then(({data}) => {
					this.order = data || {orderNo: '', amount: null};
					this.startPolling();
				}).finally(() => {
					this.ordering = false;
				})
			},
			startPolling() {
				this.stopPolling();
				this.payTimer = setInterval(() => {
					this.$api.payment.orderStatus(this.order.orderNo).then(({data}) => {
						if (data && data.status === 1) {
							this.stopPolling();
							this.$Message.success('支付成功，已开通超级会员');
							this.showVipModal = false;
							this.order = {orderNo: '', amount: null};
							this.$store.dispatch('init');
							this.loadMemberInfo();
						}
					}).catch(() => {
					})
				}, 3000);
			},
			stopPolling() {
				if (this.payTimer) {
					clearInterval(this.payTimer);
					this.payTimer = null;
				}
			},
			doActivate() {
				let code = (this.activateCode || '').trim().toUpperCase();
				if (!code) {
					this.$Message.error('请输入激活码');
					return;
				}
				if (!/^[A-Z0-9][A-Z0-9-]{4,31}$/.test(code)) {
					this.$Message.error('激活码格式不正确，应为 5-32 位字母、数字或短横线');
					return;
				}
				if (this.member.vip) {
					this.$Message('您已是超级会员，无需重复激活');
					return;
				}
				this.activating = true;
				this.$api.member.activate(code).then(({msg}) => {
					this.$Message.success(msg || '激活成功，已升级超级会员');
					this.showVipModal = false;
					this.activateCode = '';
					this.activating = false;
					this.$store.dispatch('init');
					this.loadMemberInfo();
				}).catch(() => {
					this.activating = false;
				})
			},

			/* ===== 格式化 ===== */
			monthFormat(val) {
				return val ? moment(val).format("YYYY年MM月") : '';
			},
			dateFormat(val) {
				return val ? moment(val).format("YYYY-MM-DD") : '';
			},
			//到期时间标色：已过期红色，7天内到期橙色
			expireClass(val) {
				let diff = moment(val).diff(moment(), 'days');
				if (diff < 0) return 'expire-expired';
				if (diff <= 7) return 'expire-soon';
				return '';
			},
			timeFormat(val) {
				return val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : '';
			}
		},
		mounted() {
			this.loadList();
			this.loadMemberInfo();
		},
		beforeDestroy() {
			this.stopPolling();
		}
	};
</script>

<style scoped>
	.renew-pay {
		margin-top: 14px;
		padding: 12px;
		background: #f8f9fb;
		border-radius: 8px;
		text-align: center;

		.renew-pay-title {
			font-weight: bold;
		}

		.renew-pay-price {
			color: #f5222d;
			font-size: 20px;
			font-weight: bold;
			margin: 4px 0;
		}

		.renew-pay-qr {
			width: 160px;
			height: 160px;
			object-fit: contain;
			margin: 6px auto;
			display: block;
		}
	}

	.account-page {
		.filter-bar {
			display: flex;
			align-items: center;
			padding: 10px 12px;
			flex-wrap: wrap;
			gap: 8px;
		}

		.filter-input {
			width: 170px;
		}

		.filter-select {
			width: 150px;
		}

		.filter-right {
			margin-left: auto;
			display: flex;
			align-items: center;
		}

		.company-cell {
			display: flex;
			flex-direction: column;
			line-height: 1.6;
		}

		.company-name {
			font-weight: bold;
		}

		.company-sub {
			color: #999;
			font-size: 12px;
		}

		.row-actions {
			.action-link {
				margin-right: 8px;
				cursor: pointer;
				color: #2db7f5;
			}

			.action-link:hover {
				text-decoration: underline;
			}
		}

		.advanced-toggle {
			text-align: center;
			padding: 8px 0;
			border-top: 1px dashed #e8e8e8;
			margin-top: 10px;
		}

		.role-desc {
			color: #999;
			font-size: 12px;
			padding-left: 20px;
		}

		.tax-config {
			background: #fafafa;
			padding: 10px;
			margin-bottom: 10px;
		}

		.tax-actions {
			padding-bottom: 10px;
		}

		.login-ok {
		color: #19be6b;
	}

	.login-no {
		color: #999;
	}

	.expire-expired {
		color: #f5222d;
		font-weight: bold;
	}

	.expire-soon {
		color: #fa8c16;
	}

		.backup-tip {
			color: #ff9900;
			background: #fff8e6;
			padding: 8px 12px;
			margin-bottom: 10px;
			border-radius: 4px;
			font-size: 13px;
		}
	}

	/* VIP 弹窗样式 */
	.vip-modal {
		width: 720px;
	}

	.vip-body {
		display: flex;
	}

	.vip-card {
		width: 260px;
		flex-shrink: 0;
		background: linear-gradient(135deg, #3b8beb 0%, #2563c7 100%);
		border-radius: 8px;
		padding: 20px;
		color: #fff;
	}

	.vip-card-row {
		display: flex;
		justify-content: space-between;
		margin-bottom: 14px;
		font-size: 13px;
	}

	.vip-card-label {
		opacity: 0.8;
	}

	.vip-card-value {
		font-weight: bold;
	}

	.vip-card-price {
		border-top: 1px solid rgba(255, 255, 255, 0.3);
		padding-top: 14px;
		margin-bottom: 0;
		align-items: center;
	}

	.vip-price {
		font-size: 20px;
		margin-right: 8px;
	}

	.vip-price-old {
		opacity: 0.6;
		font-size: 12px;
		font-weight: normal;
	}

	.vip-pay {
		width: 180px;
		flex-shrink: 0;
		margin-left: 20px;
		text-align: center;
	}

	.vip-pay-title {
		font-weight: bold;
		margin-bottom: 10px;
	}

	.vip-pay-qr {
		width: 160px;
		height: 160px;
		border: 1px solid #e8e8e8;
		border-radius: 4px;
	}

	.vip-pay-amount {
		color: #f50;
		font-size: 18px;
		font-weight: bold;
		margin: 10px 0;
	}

	.vip-pay-order {
		font-size: 12px;
		color: #666;
		word-break: break-all;
	}

	.vip-pay-tip {
		font-size: 12px;
		color: #999;
		margin-top: 6px;
	}

	.vip-pay-empty {
		color: #999;
		font-size: 13px;
		padding: 60px 10px;
		border: 1px dashed #e8e8e8;
		border-radius: 4px;
	}

	.vip-compare {
		flex: 1;
		margin-left: 20px;
	}

	.vip-compare-title {
		font-weight: bold;
		margin-bottom: 10px;
	}

	.vip-compare table {
		width: 100%;
		border-collapse: collapse;
		font-size: 13px;
	}

	.vip-compare th, .vip-compare td {
		border: 1px solid #e8e8e8;
		padding: 8px 10px;
		text-align: left;
	}

	.vip-compare th {
		background: #f7f7f7;
	}

	.vip-yes {
		color: #3b8beb;
		font-weight: bold;
	}

	.vip-activate {
		display: flex;
		margin-top: 20px;
	}

	.vip-activate-input {
		flex: 1;
		margin-right: 10px;
	}

	.vip-tip {
		margin-top: 10px;
		color: #999;
		font-size: 12px;
		text-align: center;
	}
</style>
